import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { isServerError } from 'src/utils/request';
import { getIdentityLabel } from 'components/settings/model/Settings';
import { PasswordFormField } from 'lib/form/PasswordFormField';
import { TextFormField } from 'lib/form/TextFormField';
import logger from 'lib/logger';
import { useLocalHistory } from 'lib/useLocalHistory';
import { Link } from 'lib/Link';
import { ROUTES } from 'pages/routes';
import { finalizeSignIn } from 'store/auth/authSlice';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';
import { userApi } from '../userApi';
import { getIdentityValidationScheme, UserData } from '../model/User';
import { useSnackbar } from 'notistack';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

export interface ForgotPasswordFormProps {
    defaultIdentity?: string;
    noPadding?: boolean;
    onSuccess?: (res: UserData) => void;
    onSwitchToLogIn?: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
    defaultIdentity = '',
    noPadding = false,
    onSuccess,
    onSwitchToLogIn
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { logUserEvent } = useGAHelpers();
    const { getParsedPath } = useLocalHistory();

    const [step, setStep] = React.useState<'reset' | 'create'>('reset');
    const [identity, setIdentity] = React.useState(defaultIdentity);

    const identityLabel = React.useMemo(
        () => getIdentityLabel(settings?.emailAuthEnabled, settings?.phoneAuthEnabled),
        [settings?.emailAuthEnabled, settings?.phoneAuthEnabled]
    );

    const resetValidation = React.useMemo(
        () =>
            Yup.object().shape({
                identity: getIdentityValidationScheme(identityLabel, settings?.region.phoneNumberCode, t)
            }),
        [identityLabel, settings?.region.phoneNumberCode, t]
    );

    const handleResetSubmit = React.useCallback(
        ({ identity: id }: { identity: string }) => {
            logUserEvent(EGAEventName.ResetPasswordSubmit);
            setIdentity(id);
            setStep('create');
        },
        [logUserEvent]
    );

    React.useEffect(() => {
        if (step !== 'create' || !identity) {
            return;
        }
        userApi.resetPassword(identity).catch(e => {
            logger.error(e);
            if (isServerError(e?.status)) {
                enqueueSnackbar(t('GENERAL_ERROR_PROCESSING_REQUEST'), { variant: 'error' });
                setStep('reset');
            }
        });
    }, [enqueueSnackbar, identity, step, t]);

    const createValidation = React.useMemo(
        () =>
            Yup.object().shape({
                code: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                password: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
            }),
        [t]
    );

    const hasAppUserAuth = React.useMemo(() => !!settings?.iam?.appUserAuth, [settings?.iam?.appUserAuth]);

    const handleCreateSubmit = React.useCallback(
        async ({ code, password }: { code: string; password: string }) => {
            try {
                const userData = await userApi.createNewPassword(identity, code, password);
                logUserEvent(EGAEventName.CreatePasswordSubmit);
                dispatch(finalizeSignIn(userData));
                onSuccess?.(userData);
            } catch (e: any) {
                logger.error(e);
                const generalErrorMessage = e?.data?.message || t('GENERAL_ERROR_PROCESSING_REQUEST');
                let errorMessage: string;
                switch (e?.status) {
                    case 401:
                        errorMessage = t('DIALOG_RESET_UNAUTHORISED_MESSAGE');
                        break;
                    case 422:
                        errorMessage = hasAppUserAuth
                            ? generalErrorMessage
                            : t('DIALOG_RESET_UNAUTHORISED_MESSAGE');
                        break;
                    default:
                        errorMessage = generalErrorMessage;
                }
                if (e?.data?.code === 'E-WAF-0001') {
                    errorMessage = e.data.message;
                }
                enqueueSnackbar(errorMessage, { variant: 'error' });
            }
        },
        [dispatch, enqueueSnackbar, hasAppUserAuth, identity, logUserEvent, onSuccess, t]
    );

    const handleSignInLinkClick = React.useCallback(
        (e: React.MouseEvent) => {
            if (onSwitchToLogIn) {
                e.preventDefault();
                onSwitchToLogIn();
            }
        },
        [onSwitchToLogIn]
    );

    if (step === 'reset') {
        return (
            <Box px={noPadding ? 0 : 2}>
                <Formik
                    initialValues={{ identity }}
                    validationSchema={resetValidation}
                    onSubmit={handleResetSubmit}
                    enableReinitialize
                >
                    {({ submitForm, isSubmitting, isValid, values: { identity: id } }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <Typography variant="body2">
                                        {t('ONBOARDING_RESET_MESSAGE', { submit: t('BUTTON_SUBMIT') })}
                                    </Typography>
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField
                                        name="identity"
                                        InputProps={{ autoComplete: 'email' }}
                                        label={t(`SIGN_UP_LABEL_${identityLabel.toUpperCase()}`)}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <LoadingButton
                                        onClick={submitForm}
                                        disabled={isSubmitting || !isValid || !id}
                                        loading={isSubmitting}
                                        variant="contained"
                                        fullWidth
                                        color="primary"
                                    >
                                        {t('BUTTON_SUBMIT')}
                                    </LoadingButton>
                                </Grid>
                                <Grid size={12}>
                                    <Typography align="center">
                                        {t('AUTH_ALREADY_HAVE_ACCOUNT') + ' '}
                                        <Link
                                            to={getParsedPath(ROUTES.USER.LOGIN)}
                                            onClick={handleSignInLinkClick}
                                        >
                                            {t('ONBOARDING_SIGN_IN')}
                                        </Link>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box>
        );
    }

    return (
        <Box key="create" px={noPadding ? 0 : 2}>
            <Formik
                initialValues={{ code: '', password: '' }}
                validationSchema={createValidation}
                onSubmit={handleCreateSubmit}
            >
                {({ submitForm, isSubmitting, isValid, values: { code, password } }) => (
                    <Form>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {t('ONBOARDING_RESET_CONFIRMATION_CREDENTIAL_MESSAGE', {
                                        credential: identity
                                    })}
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <TextFormField
                                    name="code"
                                    InputProps={{ autoComplete: 'off' }}
                                    label={t('ONBOARDING_RESET_CONFIRMATION_CODE')}
                                />
                            </Grid>
                            <Grid size={12}>
                                <PasswordFormField
                                    name="password"
                                    label={t('ONBOARDING_RESET_PASSWORD')}
                                    description={t('ONBOARDING_PASSWORD_STRENGTH_LABEL')}
                                    autoComplete="new-password"
                                />
                            </Grid>
                            <Grid size={12}>
                                <LoadingButton
                                    onClick={submitForm}
                                    disabled={isSubmitting || !isValid || !code || !password}
                                    loading={isSubmitting}
                                    variant="contained"
                                    fullWidth
                                    color="primary"
                                >
                                    {t('BUTTON_SUBMIT')}
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};
